import React, { useState, useContext, useEffect, useCallback, useReducer } from 'react';
import axios from 'axios';

import { Box, Step, Stepper, StepLabel, Modal, Button, Paper, Backdrop, CircularProgress } from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { toast } from 'react-toastify';

import { apiRoute } from '../../../App';
import { EntityContext } from '../../../context/EntityContext';

import CCInfo from './PaymentTabs/CCInfo';
import Disclosure from './PaymentTabs/Disclosure';
import ContactInfo from './PaymentTabs/ContactInfo';
import PaymentReview from './PaymentTabs/PaymentReview';
import DeclinedModal from './DeclinedModal';
import { CcComponent } from '../../CcComponent';
const steps = ['Contact Information', 'Credit Card Information', 'Payment Disclosure', 'Review Payment'];

const PaymentInfo = (props) => {
    const {
        paymentsArray,
        paymentAmount,
        totalPayments,
        fee,
        declinedModal,
        closeDecline,
        activeStep,
        handleClose,
        handleBack,
        handleNext,
        decline,
        defendantName,
        ts,
        caseId,
        isSmallScreen
    } = props;

    const { entity } = useContext(EntityContext);

    const [displayPhoneNumber, setDisplayPhoneNumber] = useState('');
    const [displayCreditCardNumber, setDisplayCreditCardNumber] = useState('');
    // Used to track wether required cc fields are populated on the form
    const [ccnumber, setCcNumber] = useState(false);
    const [ccExpDt, setCcExpDt] = useState(false);
    const [ccCvv, setCcCvv] = useState(false);
    const [paymentToken, setPaymentToken] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [validForm, setValidForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingT] = useState(false);
    const [paymentId, setPaymentId] = useState(null);
    const [response, setResponse] = useState(null);
    const [resetState,] = useState({
        email: '',
        phoneNumber: '',
        firstName: '',
        lastName: '',
        payment_token: '',
        billingAddress: '',
        city: '',
        state: '',
        zipCode: '',
    })

    const initialState = {
        email: '',
        phoneNumber: '',
        firstName: '',
        lastName: '',
        payment_token: '',
        billingAddress: '',
        city: '',
        state: '',
        zipCode: '',
    };

    const reducer = (state, action) => {
        switch (action.type) {
            case 'UPDATE_FIELD':
                return {
                    ...state,
                    [action.field]: action.value,
                    errors: {
                        ...state.errors,
                        [action.field]: action.error,
                    },
                };
            case 'UPDATE_FIELD_ERROR':
                return {
                    ...state,
                    errors: {
                        ...state.errors,
                        [action.field]: action.error,
                    }
                };
            case 'CLEAR_FIELD_ERROR':
                return {
                    ...state,
                    bond: (() => {
                        const updatedErrors = { ...state.errors };
                        delete updatedErrors[action.field];
                        return {
                            ...state,
                            errors: updatedErrors,
                        };
                    }),
                };
            case 'RESET_STATE':
                return resetState;
            default:
                return state;
        }
    };

    const [state, dispatch] = useReducer(reducer, initialState);

    const handleFieldChange = (field, value) => {
        dispatch({ type: 'UPDATE_FIELD', field, value });
    };

    const handleResetState = () => {
        dispatch({ type: 'RESET_STATE', resetState });
    }

    const handleBlur = (field, value) => {
        let error = null;
        switch (field) {
            case 'email':
                if (!value) {
                    error = 'Email Required'
                } else if (!/\S+@\S+\.\S+/.test(value)) {
                    error = 'Enter a valid email'
                }
                break;
            case 'phoneNumber':
                if (!!value && value.length !== 10) {
                    error = 'Phone number must be 10 digits'
                }
                break;
            case 'firstName':
                if (!value) {
                    error = 'First name is required'
                }
                break;
            case 'lastName':
                if (!value) {
                    error = 'Last name is required'
                }
                break;
            case 'billingAddress':
                if (!value) {
                    error = 'Billing address is required'
                }
                break;
            case 'city':
                if (!value) {
                    error = 'City is required'
                }
                break;
            case 'state':
                if (!value) {
                    error = 'State is required'
                }
                break;
            case 'zipCode':
                if (!value) {
                    error = 'ZipCode is required'
                }
                break;

            default:
                break;
        }
        dispatch({ type: 'UPDATE_FIELD', field, value, error });
    };

    useEffect(() => {
        const fullName = `${state?.firstName} ${state?.lastName}`;
        setDisplayName(fullName);
    }, [state?.firstName, state?.lastName]);

    // function to set validForm state to it's correct values based on the terms
    const nextButtonCheck = useCallback(() => {

        if (activeStep === 0) {
            if (state?.email === '' || state?.errors.email) {
                setValidForm(false);
            } else {
                setValidForm(true);
            }
        } else if (activeStep === 1) {
            // boolean variables to check and see if all of the form's values/errors are empty strings
            const isFormEmpty = Object.keys(state).filter((key) => key !== 'phoneNumber' && key !== 'payment_token').every((key) => state[key] !== '');
            const isErrorsEmpty = Object.values(state?.errors).some((value) => value !== null);
            // These values are set via callback from collect js
            const isCcFormValueValid = !!ccCvv && !!ccExpDt && !!ccnumber;
            setValidForm(isCcFormValueValid && !isErrorsEmpty && isFormEmpty);
        }
    }, [activeStep, ccCvv, ccExpDt, ccnumber, state]);

    // useEffect that runs the nextButtonCheck function
    useEffect(() => {
        nextButtonCheck();
    }, [nextButtonCheck]);

    const handleTokenCallback = (token) => {
        // Set the token once we have it
        setPaymentToken(token);
        handleNext();
    };

    const createTokenTriggerNextStep = () => {
        window.CollectJS.startPaymentRequest(handleTokenCallback);
    }

    const isCreditCardStep = (steps, activeStep) => {
        return steps[activeStep] === "Credit Card Information";
    };

    // array of the components that render in each tab
    const stepContent = [
        <ContactInfo
            state={state}
            handleFieldChange={handleFieldChange}
            handleBlur={handleBlur}
            displayPhoneNumber={displayPhoneNumber}
            setDisplayPhoneNumber={setDisplayPhoneNumber}
            ts={ts}
            isSmallScreen={isSmallScreen}
        />,
        <CCInfo
            state={state}
            handleFieldChange={handleFieldChange}
            handleBlur={handleBlur}
            displayCreditCardNumber={displayCreditCardNumber}
            setDisplayCreditCardNumber={setDisplayCreditCardNumber}
            ts={ts}
            isSmallScreen={isSmallScreen}
            setCcNumber={setCcNumber}
            setCcCvv={setCcCvv}
            setCcExpDt={setCcExpDt}
            handleTokenCallback={ handleTokenCallback }
        />,
        <Disclosure />,
        <PaymentReview
            paymentsArray={paymentsArray}
            name={state.name}
            email={state.email}
            displayPhoneNumber={displayPhoneNumber}
            creditCardNumber={state?.creditCardNumber}
            paymentAmount={paymentAmount}
            totalPayments={totalPayments}
            fee={fee}
            displayName={displayName}
            defendantName={defendantName}
            isSmallScreen={isSmallScreen}
            handleResetState={handleResetState}
        />
    ];

    const createPayment = async (payment, transactionId, ccPaymentId) => {
        const newPayment = {
            FkCaseId: caseId,
            FkViolationId: payment.pkViolationId,
            PaymentRef: payment.currentStatute,
            PaymentAmount: parseFloat(payment.payment),
            ShiftDate: new Date(),
            PaymentType: 'CREDIT CARD',
            BatchNumber: '1234',
            PayToName: `${state?.firstName} ${state?.lastName}`,
            PayToAddress1: state?.billingAddress,
            PayToCity: state?.city,
            PayToState: state?.state,
            PayToZip: state?.zipCode,
            ConvenienceFee: fee,
            AuthorizationNumber: transactionId,
            CCPaymentId: ccPaymentId
        };
        try {
            const res = await axios.post(`${apiRoute}/api/ffpaymenttbls/${entity.id}`, newPayment);
            setPaymentId(res?.data?.pkFfPaymentId)
        } catch (err) {
            console.log(err)
        }
    };

    const createPayments = async (res) => {
        try {
            for (const payment of paymentsArray) {
                await createPayment(payment, res.data.paymentConfirmation.transactionId, res.data.id);
            }
        } catch (err) {
            console.log(err);
            decline();
        }
    }

    const handleSubmit = async () => {
        setLoading(true);
        const payment_token = paymentToken;
        const adjustedPaymentAmount = (parseFloat(paymentAmount) - parseFloat(fee)).toString();
        const ccPaymentData = {
                PaymentToken: payment_token,
                CardHolderEmail: state?.email,
                CardHolderPhone: state?.phoneNumber,
                CardHolderName: `${state?.firstName} ${state?.lastName}`,
                CardHolderAddressLine1: state?.billingAddress,
                CardHolderCity: state?.city,
                CardHolderState: state?.state,
                CardHolderZip: state?.zipCode,
                PaymentAmount: adjustedPaymentAmount,
                ConvenienceFee: fee.toString()
        };

        try {
            const res = await axios.post(`${apiRoute}/api/CcPayments/${entity.id}/payment`, ccPaymentData);

            if (res.data.paymentConfirmation.response !== '1') {
                setResponse(res.data.paymentConfirmation);
                return decline();
            }
            await createPayments(res);
            handleNext();
        } catch (err) {
            console.error(err);
            decline();

        } finally {
            setLoading(false)
        }
    };

    /**
     * 
     * Builds all request parameters for the print api call to backend
     * 
     * @returns URLSearchParams object
     */
    const getPaymentReceiptParams = () => {
        const cardholderName = `${state?.firstName} ${state?.lastName}`;
        // map through the payments Array pulling currentStatute, description, and payment
        const ViolationDtl = paymentsArray.map((payment) => {
            let details = { currentStatute: payment.currentStatute, description: payment.description, payment: payment.payment };
            return details;
        });
        const ViolationDtlJSON = JSON.stringify(ViolationDtl);

        // parameters
        const searchParams = new URLSearchParams();
        if (caseId) searchParams.set('caseId', caseId);
        if (paymentId) searchParams.set('paymentId', paymentId);
        if (cardholderName) searchParams.set('cardholderName', cardholderName);
        if (state?.email) searchParams.set('cardholderEmail', state?.email);
        if (paymentAmount) searchParams.set('total', paymentAmount);
        if (fee) searchParams.set('ccFeeAmount', fee);
        if (totalPayments) searchParams.set('subTotal', totalPayments);
        if (ViolationDtlJSON) searchParams.set('violationDtlJson', ViolationDtlJSON);
        if (defendantName) searchParams.set('defendantName', defendantName);
        if (entity.entityName) searchParams.set('sherOffice', entity.entityName);
        if (entity.id) searchParams.set('entityId', entity.id);

        return searchParams;
    };

    const printReceipt = () => {
        setLoading(true);

        // parameters
        const searchParams = getPaymentReceiptParams();

        axios.get(`${apiRoute}/api/PaymentReceipt?${searchParams.toString()}`, { responseType: 'arraybuffer' })
            .then(({ data }) => {
                const arrayBufferView = new Uint8Array(data);
                const blob = new Blob([arrayBufferView], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
            }).catch((e) => console.log(e))
            .finally(() => setLoading(false));
    };

    const emailReceipt = () => {
        setLoading(true);

        // parameters
        const searchParams = getPaymentReceiptParams();

        axios.post(`${apiRoute}/api/PaymentReceipt/emailCheck?${searchParams.toString()}`, false, {
            headers: { 'Content-Type': 'application/json' },
        })
            .then(() => {
                toast.success(`A copy of the receipt was emailed to ${state?.email}`, {
                    position: "top-right",
                    autoClose: 2000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "colored",
                });
            })
            .catch((err) => {
                toast.error(`Error ${err.response.status} while trying to email receipt`, {
                    position: "top-right",
                    autoClose: 2000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "colored",
                });
            })
            .finally(() => setLoading(false));
    };

    // In the event the user clicks 'back' from the CC payment
    // info screen, we want to clear the field validations
    // since the collject js iframes are destroyed
    const handleBackAndClearCc = () => {

        setCcNumber(false);
        setCcExpDt(false);
        setCcCvv(false);

        handleBack();
    }

    return (
        <CcComponent>
        <Paper elevation={10} sx={{ height: isSmallScreen ? '100%' : 'auto', overflowY: 'auto' }} display="flex" flexDirection={isSmallScreen ? 'column' : 'column'} >
            <Paper sx={{ textAlign: 'center', backgroundColor: 'steelblue', color: 'white', p: '1vh', mb: '2vh' }} elevation={10}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '95%', m: '0 auto' }}>
                    {
                        isSmallScreen ?
                            <>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                    <div onClick={handleClose}>
                                        <h2 style={{ margin: 0, cursor: 'pointer' }}>
                                            <ArrowBackIcon sx={{ fontSize: '2rem' }} />
                                        </h2>
                                        Exit
                                    </div>
                                </Box>
                                <h3 style={{ flex: 1, margin: 0 }}>Payment Info</h3>
                            </>
                            :
                            <>
                                <h3 style={{ flex: 1, margin: 0 }}>Payment Info</h3>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <div onClick={handleClose}>
                                        <h2 style={{ margin: 0, cursor: 'pointer' }}>
                                            <CloseIcon sx={{ fontSize: '2rem' }} />
                                        </h2>
                                    </div>
                                </Box>
                            </>
                    }
                </Box>
            </Paper>
            <Stepper activeStep={activeStep} orientation={isSmallScreen ? "vertical" : "horizontal"} sx={{ m: '1vh 7vw', p: 'auto' }}>
                {steps.map((label) => {
                    const stepProps = {};
                    const labelProps = {};
                    return (
                        <Step key={label} {...stepProps}>
                            <StepLabel {...labelProps} sx={{ '& .MuiStepLabel-label': { fontSize: isSmallScreen ? '1rem' : null } }}>{label}</StepLabel>
                        </Step>
                    );
                })}
            </Stepper>
            <hr />
            {activeStep === steps.length ? (
                <React.Fragment>
                    <Box sx={{ mt: 2, mb: 1, px: 1 }}>
                        <h4 style={{ textAlign: 'center', marginBotton: '1vh' }}>Payment Accepted</h4>
                            <p>Thank you for your payment. Should you have any billing questions or concerns regarding this payment and/or the amount debited from your account, please contact smartcollectmerchant@praeses.com</p>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: isSmallScreen ? 'column' : 'row', gap: '1rem', p: 1 }}>
                        <Box sx={{ flex: '1 1 auto' }} />
                        <Button
                            disabled={loadingT}
                            onClick={emailReceipt}
                            variant="contained"
                            sx={{ backgroundColor: 'steelblue', color: 'white', height: isSmallScreen ? '7.5vh' : null, width: isSmallScreen ? '100%' : null }}
                        >
                            {
                                loadingT
                                    ? <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                                        <CircularProgress size={20} />
                                    </Box>
                                    : 'Email Receipt'
                            }
                        </Button>
                        <Button
                            onClick={printReceipt}
                            variant="contained"
                            sx={{ backgroundColor: 'steelblue', color: 'white', height: isSmallScreen ? '7.5vh' : null, width: isSmallScreen ? '100%' : null }}
                        >
                            Print Receipt
                        </Button>
                    </Box>
                </React.Fragment>
            ) : (
                <React.Fragment>
                    <form onSubmit={handleSubmit} id="paymentModalForm">
                        <Box sx={{ mt: 2, mb: 1 }}>{stepContent[activeStep]}</Box>
                        <Box sx={{ display: 'flex', flexDirection: isSmallScreen ? 'column' : 'row', px: 2, py: .5, gap: isSmallScreen ? '.5rem' : null, mb: '1vh' }}>
                            <Button
                                color="inherit"
                                disabled={activeStep === 0}
                                onClick={handleBackAndClearCc}
                                sx={{ backgroundColor: 'gray', width: isSmallScreen ? '100%' : null, height: isSmallScreen ? '7.5vh' : null }}
                                variant="contained"
                                startIcon={<ArrowBackIcon />}
                            >
                                Back

                            </Button>
                            <Box sx={{ flex: '1 1 auto' }} />
                                    {activeStep === steps.length - 1
                                        ?
                                        <Box>
                                            <Button
                                                onClick={handleSubmit}
                                                variant='contained'
                                                id='payButton'
                                                sx={{ backgroundColor: 'green', color: 'white', width: isSmallScreen ? '100%' : null, height: isSmallScreen ? '7.5vh' : null }}

                                            >
                                                <strong>Confirm Payment</strong>
                                            </Button>
                                        </Box>
                                        :
                                        <Button
                                            id={isCreditCardStep(steps, activeStep) ? "payButton" : "nextButton"}
                                            onClick={isCreditCardStep(steps, activeStep) ? createTokenTriggerNextStep : handleNext}
                                            variant='contained'
                                            sx={{ backgroundColor: 'steelblue', color: 'white', width: isSmallScreen ? '100%' : null, height: isSmallScreen ? '7.5vh' : null }}
                                            disabled={!validForm}
                                        >
                                            Next
                                        </Button>
                            }
                        </Box>
                    </form>
                    {
                        declinedModal && (
                            <Modal open={declinedModal} onClose={closeDecline} sx={{ height: isSmallScreen ? '35%' : '25%', width: isSmallScreen ? "85%" : '25%', margin: isSmallScreen ? '25vh auto' : "10vh auto", backgroundColor: 'red', borderRadius: '2%', display: 'flex' }}>
                                <>
                                    <DeclinedModal
                                        closeDecline={closeDecline}
                                        response={response}
                                        handleBack={handleBack}
                                        setResponse={setResponse}
                                    />
                                </>
                            </Modal>
                        )
                    }
                </React.Fragment>
            )}
            {loading && (
                <Backdrop open sx={{ zIndex: 1 }}>
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                        <p>
                            <CircularProgress />
                        </p>
                    </div>
                </Backdrop>
            )}
            </Paper>
        </CcComponent>
    );
};

export default PaymentInfo;